import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import { updateAllFromKit, uninstallFormQuery, updateFormQuery, type InstalledDevice, type UpdateKit } from '../api/softseeWrites'
import { apiPost, ApiError } from '../api/client'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import { deviceIcon } from '../alarms/AlarmDetails'
import { fieldsOf } from './softseeWriteForm'

function Tile({ device, children }: { device: InstalledDevice; children?: ReactNode }) {
  return (
    <div className="col-md-4">
      <div className="tiles-2 bg-normal">
        <div className="image-box">
          <div className="image">
            <i className={`${deviceIcon(device.Tip)} rounded success`}></i>
          </div>
        </div>
        <div className="content">
          <div className="title">
            <strong>{device.Denumire}</strong>
          </div>
          <div className="details">
            <span className="fas fa-user"></span> {String(device.Prenume ?? '').toUpperCase()} {device.Nume ?? ''}
          </div>
          <div className="details">
            <span className="far fa-save color-warning"></span> v{device.version}
          </div>
          <div className="details">{children}</div>
        </div>
      </div>
    </div>
  )
}

async function postTile(form: HTMLFormElement, path: string, success: string): Promise<void> {
  if (!confirm('Are you sure?')) return
  try {
    await apiPost(path, fieldsOf(form))
    toast(success, 'Success', 'success')
  } catch {
    toast('Something went wrong.', 'Error', 'error')
  }
}

/**
 * Softsee::uninstallApplicationDevices. Placile POST-uiesc pe capatul de kit
 * (application_id + device_id), nu rescriu uninstallApplication.
 */
export function UninstallApplicationDevices({ id }: { id: number }) {
  const page = useQuery(uninstallFormQuery(id))
  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />
  const devices = page.data.devices

  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Uninstall</strong> application
        </h4>
      </header>
      <div className="panel-body">
        <div className="row">
          {devices.length === 0 ? (
            <div className="alert alert-info">
              <span className="fas fa-info-circle"></span> No devices.
            </div>
          ) : (
            devices.map((device) => (
              <Tile key={`${device.ID}-${device.deviceId}`} device={device}>
                {Number(device.uninstallable) === 1 ? (
                  <form
                    className="uninstallApplicationForm"
                    action="/softsee/uninstallApplication"
                    method="post"
                    onSubmit={(e) => {
                      e.preventDefault()
                      void postTile(
                        e.currentTarget,
                        `/devices/${device.deviceId}/softsee-uninstall/application`,
                        'Uninstall command has been sent.',
                      )
                    }}
                  >
                    <input type="hidden" name="application_id" value={device.ID} />
                    <input type="hidden" name="device_id" value={device.deviceId} />
                    <button type="submit" className="btn btn-xs btn-danger">
                      <i className="fas fa-times-circle"></i> Uninstall
                    </button>
                  </form>
                ) : null}
              </Tile>
            ))
          )}
        </div>
      </div>
    </>
  )
}

function outdated(device: InstalledDevice, kit: UpdateKit): boolean {
  const appVersion = String(device.version ?? '').replaceAll('.', '')
  const kitVersion = String(kit.version ?? '').replaceAll('.', '')
  return kitVersion > appVersion
}

/**
 * Softsee::updateApplicationDevices. Placile POST-uiesc pe capatul de kit
 * (software + device + type). Update All scrie updateAllFromKit.
 */
export function UpdateApplicationDevices({ id }: { id: number }) {
  const query = useQueryClient()
  const page = useQuery(updateFormQuery(id))
  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />
  const kit = page.data.kit
  const devices = page.data.devices
  const stale = kit ? devices.filter((d) => outdated(d, kit)) : []

  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Update</strong> application
        </h4>
      </header>
      <div className="panel-body">
        {kit && stale.length > 0 ? (
          <UpdateAllFromKitForm
            kit={kit}
            devices={stale}
            onSent={() => void query.invalidateQueries({ queryKey: ['softsee', 'updateApplicationDevices', id] })}
          />
        ) : null}
        <div className="row">
          {!kit || devices.length === 0 ? (
            <div className="alert alert-info">
              <span className="fas fa-info-circle"></span> No devices or available kit.
            </div>
          ) : stale.length === 0 ? (
            <div className="alert alert-info">
              <span className="fas fa-info-circle"></span> No outdated applications.
            </div>
          ) : (
            stale.map((device) => (
              <Tile key={`${device.ID}-${device.deviceId}`} device={device}>
                <form
                  className="updateKit"
                  method="post"
                  action="/softsee/updateFromKit"
                  onSubmit={(e) => {
                    e.preventDefault()
                    void postTile(e.currentTarget, `/devices/${device.deviceId}/softsee-updates/kit`, 'Update command has been sent.')
                  }}
                >
                  <input name="software" value={kit.ID} type="hidden" />
                  <input name="device" value={device.deviceId} type="hidden" />
                  <input name="type" value={kit.type} type="hidden" />
                  <button className="btn btn-xs btn-primary">
                    <i className="fa fa-redo"></i> Update
                  </button>
                </form>
              </Tile>
            ))
          )}
        </div>
      </div>
    </>
  )
}

/**
 * `compact`: butonul mic din antetul filei Devices
 * (applications/details/devices.php, `btn-xs btn-primary pull-right update-all`),
 * care in vechi trimitea TOATE masinile cu aplicatia, nu doar pe cele in urma.
 * Formularul e acelasi; doar butonul arata altfel. Lista are nevoie numai de
 * `deviceId` - atat trimite si portalul (`devices[][deviceId]`).
 */
export function UpdateAllFromKitForm({ kit, devices, onSent, compact = false }: {
  kit: UpdateKit
  devices: Pick<InstalledDevice, 'deviceId'>[]
  onSent?(): void
  compact?: boolean
}) {
  const [busy, setBusy] = useState(false)

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (busy) return
    if (!confirm('Are you sure?')) return
    setBusy(true)
    try {
      const body = await updateAllFromKit({
        kitId: kit.ID,
        kitType: kit.type,
        devices: devices.map((d) => ({ deviceId: d.deviceId })),
      })
      toast(body.message || 'All devices have been updated.', 'Success', 'success')
      onSent?.()
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="updateAllFromKit" action="/softsee/updateAllFromKit" method="post" onSubmit={onSubmit} style={compact ? undefined : { marginBottom: 12 }}>
      <input type="hidden" name="kitId" value={kit.ID} />
      <input type="hidden" name="kitType" value={kit.type} />
      {devices.map((device, i) => (
        <input key={device.deviceId} type="hidden" name={`devices[${i}][deviceId]`} value={device.deviceId} />
      ))}
      {compact ? (
        <button type="submit" className="btn btn-xs btn-primary pull-right update-all" disabled={busy}>
          Update All
        </button>
      ) : (
        <button type="submit" className="btn btn-primary" disabled={busy}>
          <i className="fa fa-redo"></i> Update all
        </button>
      )}
    </form>
  )
}
